import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Id } from '@/convex/_generated/dataModel';

interface MiniPostAnalyticsProps {
	postId?: string;
	platform?: string;
	profileKey?: string;
	contentHistoryId?: Id<'contentHistory'>;
	className?: string;
}

interface AnalyticMetric {
	value: number;
	label: string;
	icon: string;
	color: string;
}

export function MiniPostAnalytics({
	postId,
	platform,
	profileKey,
	contentHistoryId,
	className,
}: MiniPostAnalyticsProps) {
	const storedAnalytics = useQuery(
		api.analytics.getStoredAnalytics,
		contentHistoryId ? { contentHistoryId } : 'skip'
	);

	const getMetrics = React.useCallback((): AnalyticMetric[] => {
		if (!postId || !platform || !profileKey || !contentHistoryId) {
			return [];
		}

		const metrics: AnalyticMetric[] = [];

		// Twitter metrics (handle both 'twitter' and 'twitterx' platforms)
		if (platform === 'twitter' || platform === 'twitterx') {
			metrics.push(
				{
					value: storedAnalytics?.likes || 0,
					label: 'Likes',
					icon: '❤️',
					color: 'from-red-400 to-pink-600',
				},
				{
					value: storedAnalytics?.views || 0,
					label: 'Views',
					icon: '🔭',
					color: 'from-purple-400 to-violet-600',
				},
				{
					value: storedAnalytics?.shares || 0,
					label: 'Retweets',
					icon: '🔄',
					color: 'from-green-400 to-emerald-600',
				},
				{
					value: storedAnalytics?.comments || 0,
					label: 'Comments',
					icon: '💬',
					color: 'from-blue-400 to-indigo-600',
				}
			);
		}

		// LinkedIn metrics
		if (platform === 'linkedin') {
			metrics.push(
				{
					value: storedAnalytics?.likes || 0,
					label: 'Likes',
					icon: '👍',
					color: 'from-blue-400 to-blue-600',
				},
				{
					value: storedAnalytics?.views || 0,
					label: 'Views',
					icon: '🔭',
					color: 'from-indigo-400 to-indigo-600',
				},
				{
					value: storedAnalytics?.comments || 0,
					label: 'Comments',
					icon: '💬',
					color: 'from-green-400 to-emerald-600',
				},
				{
					value: storedAnalytics?.shares || 0,
					label: 'Shares',
					icon: '🔄',
					color: 'from-purple-400 to-violet-600',
				}
			);
		}

		return metrics;
	}, [platform, storedAnalytics, postId, profileKey, contentHistoryId]);

	if (!postId || !platform || !profileKey || !contentHistoryId) {
		return null;
	}

	const metrics = getMetrics();

	return (
		<div className={cn('flex items-center gap-3', className)}>
			{(platform === 'twitter' || platform === 'twitterx') && (
				<>
					<MetricBadge
						label='Likes'
						icon='❤️'
						value={metrics.find((m) => m.label === 'Likes')?.value}
						color='from-red-400 to-pink-600'
					/>
					<MetricBadge
						label='Views'
						icon='🔭'
						value={metrics.find((m) => m.label === 'Views')?.value}
						color='from-purple-400 to-violet-600'
					/>
					<MetricBadge
						label='Retweets'
						icon='🔄'
						value={
							metrics.find((m) => m.label === 'Retweets')?.value
						}
						color='from-green-400 to-emerald-600'
					/>
					<MetricBadge
						label='Comments'
						icon='💬'
						value={
							metrics.find((m) => m.label === 'Comments')?.value
						}
						color='from-blue-400 to-indigo-600'
					/>
				</>
			)}
			{platform === 'linkedin' && (
				<>
					<MetricBadge
						label='Likes'
						icon='👍'
						value={metrics.find((m) => m.label === 'Likes')?.value}
						color='from-blue-400 to-blue-600'
					/>
					<MetricBadge
						label='Views'
						icon='🔭'
						value={metrics.find((m) => m.label === 'Views')?.value}
						color='from-indigo-400 to-indigo-600'
					/>
					<MetricBadge
						label='Comments'
						icon='💬'
						value={
							metrics.find((m) => m.label === 'Comments')?.value
						}
						color='from-green-400 to-emerald-600'
					/>
					<MetricBadge
						label='Shares'
						icon='🔄'
						value={metrics.find((m) => m.label === 'Shares')?.value}
						color='from-purple-400 to-violet-600'
					/>
				</>
			)}
		</div>
	);
}

interface MetricBadgeProps {
	label: string;
	icon: string;
	value?: number;
	color: string;
}

function MetricBadge({ label, icon, value, color }: MetricBadgeProps) {
	return (
		<Badge
			variant='outline'
			className={cn(
				'flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-gradient-to-b text-white whitespace-nowrap',
				color
			)}
		>
			<span className='opacity-90'>{label}</span>
			<span className='text-base'>{icon}</span>
			<span className='font-semibold'>
				{value === undefined || value === 0
					? '--'
					: value.toLocaleString()}
			</span>
		</Badge>
	);
}
